import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import CreateApi from '../Settings/appSettings';
import type { EventPushMessage } from '@moment-golf/types';
import { resolveActiveMessages } from '@moment-golf/types';

const STORAGE_KEY = 'mgt-ls-msg-acks';
const POLL_INTERVAL_MS = 30_000;

/** eventID → { pushedAt → ackedAt } */
type AckStore = Record<string, Record<string, string>>;

function isLegacyFlatAckStore(raw: unknown): raw is Record<string, string> {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
    const values = Object.values(raw as Record<string, unknown>);
    return values.length === 0 || values.every(v => typeof v === 'string');
}

function readAckStore(): AckStore {
    try {
        const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
        if (isLegacyFlatAckStore(raw)) {
            return { __legacy__: raw };
        }
        return raw as AckStore;
    } catch {
        return {};
    }
}

function getAcksForEvent(eventID: string | undefined): Record<string, string> {
    if (!eventID) return {};
    const store = readAckStore();
    return { ...(store.__legacy__ ?? {}), ...(store[eventID] ?? {}) };
}

function writeAck(eventID: string, pushedAt: string, ackedAt: string): void {
    const store = readAckStore();
    if (!store[eventID]) store[eventID] = {};
    store[eventID][pushedAt] = ackedAt;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

function isAcknowledged(msg: EventPushMessage, acks: Record<string, string>): boolean {
    const ackedAt = acks[msg.pushedAt];
    return !!ackedAt && ackedAt >= msg.pushedAt;
}

/** Returns true if the message targets this user based on their scoreCode and teeTime slots. */
function isTargeted(
    msg: EventPushMessage,
    scoreCode: string,
    registrationData: any[],
): boolean {
    const target = msg.target;
    if (target === 'all') return true;

    if (target.type === 'players') {
        return target.scoreCodes.includes(scoreCode);
    }

    if (target.type === 'teeTime') {
        const targetSlots: Array<{ teeTime: string; round: number }> =
            Array.isArray((target as any).slots)
                ? (target as any).slots
                : (target as any).teeTime != null
                    ? [{ teeTime: (target as any).teeTime, round: (target as any).round }]
                    : [];

        if (targetSlots.length === 0) return false;

        return (registrationData ?? []).some((reg: any) => {
            if (reg.scoreCode !== scoreCode) return false;
            return (reg.teeTimes ?? []).some((slot: any) => {
                const slotTeeTime = typeof slot.teeTime === 'string'
                    ? slot.teeTime
                    : (slot.teeTime as Date).toISOString();
                return targetSlots.some(
                    s => s.round === Number(slot.round) && s.teeTime === slotTeeTime
                );
            });
        });
    }

    return false;
}

interface UseEventMessagesResult {
    activeMessages: EventPushMessage[];
    pendingMessages: EventPushMessage[];
    acknowledge: (pushedAt: string) => void;
    isAcknowledged: (pushedAt: string) => boolean;
    reviewMessage: EventPushMessage | null;
    openReview: (pushedAt: string) => void;
    closeReview: () => void;
}

export function useEventMessages(
    eventID: string | undefined,
    scoreCode: string,
    registrationData: any[],
): UseEventMessagesResult {
    const api = CreateApi();

    const [messages, setMessages] = useState<EventPushMessage[]>([]);
    const [acks, setAcks] = useState<Record<string, string>>(() => getAcksForEvent(eventID));
    const [reviewMessage, setReviewMessage] = useState<EventPushMessage | null>(null);
    const apiRef = useRef(api);
    apiRef.current = api;

    useEffect(() => {
        if (!eventID) {
            setMessages([]);
            setReviewMessage(null);
            setAcks({});
            return;
        }
        setMessages([]);
        setReviewMessage(null);
        setAcks(getAcksForEvent(eventID));
    }, [eventID]);

    const fetchMessages = useCallback(async () => {
        if (!eventID) return;
        try {
            const res = await apiRef.current.get('/ls/GetEventPushMessages', {
                params: { eventID },
            });
            const data: EventPushMessage[] = Array.isArray(res.data) ? res.data : [];
            setMessages(data);
        } catch {
            // Silently ignore polling errors
        }
    }, [eventID]);

    useEffect(() => {
        if (!eventID) return;
        fetchMessages();
        const interval = setInterval(fetchMessages, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [eventID, fetchMessages]);

    const isTargetedForUser = useCallback(
        (msg: EventPushMessage) => isTargeted(msg, scoreCode, registrationData),
        [scoreCode, registrationData],
    );

    const activeMessages = useMemo(
        () => resolveActiveMessages(messages, isTargetedForUser),
        [messages, isTargetedForUser],
    );

    const pendingMessages = useMemo(
        () => activeMessages.filter(msg => !isAcknowledged(msg, acks)),
        [activeMessages, acks],
    );

    const acknowledge = useCallback((pushedAt: string) => {
        if (!eventID) return;
        const ackedAt = new Date().toISOString();
        writeAck(eventID, pushedAt, ackedAt);
        setAcks(getAcksForEvent(eventID));
        setReviewMessage(prev => (prev?.pushedAt === pushedAt ? null : prev));
    }, [eventID]);

    const isAcknowledgedFn = useCallback(
        (pushedAt: string) => {
            const msg = activeMessages.find(m => m.pushedAt === pushedAt);
            if (!msg) return false;
            return isAcknowledged(msg, acks);
        },
        [activeMessages, acks],
    );

    const openReview = useCallback((pushedAt: string) => {
        const msg = activeMessages.find(m => m.pushedAt === pushedAt) ?? null;
        setReviewMessage(msg);
    }, [activeMessages]);

    const closeReview = useCallback(() => {
        setReviewMessage(null);
    }, []);

    return {
        activeMessages,
        pendingMessages,
        acknowledge,
        isAcknowledged: isAcknowledgedFn,
        reviewMessage,
        openReview,
        closeReview,
    };
}
